import {cn} from '@tempmee/core'
import {OutlineCheckIcon} from '@tempmee/react-icons'
import {type HTMLAttributes, type KeyboardEvent, type ReactNode, forwardRef, useId} from 'react'
import {SwitchHaloVariants, SwitchTrackVariants} from './Variants'

export interface SwitchProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'onChange'> {
  /** Whether the switch is on. */
  checked?: boolean
  /** Callback fired when the switch is toggled. */
  onChange?: (checked: boolean) => void
  /** Whether the switch is disabled. */
  disabled?: boolean
  /** On-state color — `primary` (teal) or `favorite` (purple). @default 'primary' */
  color?: 'primary' | 'favorite'
  /** Content rendered beside the switch. A string is wrapped in a styled label; any node passes through. */
  label?: ReactNode
}

/**
 * On/off toggle for a single setting. MUI `Switch`-aligned: `checked` /
 * `onChange(checked)` / `disabled` / `color`. When on, the track fills with the
 * color and reveals a white check beside the thumb.
 *
 * @see https://www.figma.com/design/11epclteWbmxaDMj2JUJW8/Design-System-Shift?node-id=183-6658
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {checked = false, onChange, disabled = false, color = 'primary', label, className, ...props},
    ref,
  ) => {
    const id = useId()
    const labelId = label ? `${id}-label` : undefined

    const handleClick = () => {
      if (!disabled) onChange?.(!checked)
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleClick()
      }
      props.onKeyDown?.(e)
    }

    return (
      <div className="inline-flex items-center gap-2">
        <button
          ref={ref}
          type="button"
          role="switch"
          aria-checked={checked}
          aria-labelledby={labelId}
          disabled={disabled}
          className={cn(
            'group relative inline-flex h-10 w-14 shrink-0 items-center bg-transparent p-0 outline-none',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            className,
          )}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          {...props}
        >
          {/* track */}
          <span aria-hidden className={cn(SwitchTrackVariants({color, checked, disabled}))} />
          {/* check — revealed beside the thumb when on */}
          {checked && (
            <OutlineCheckIcon
              aria-hidden
              width={16}
              height={16}
              className={cn(
                'pointer-events-none absolute left-[9px] top-1/2 -translate-y-1/2',
                disabled ? 'text-action-disabled' : 'text-white',
              )}
            />
          )}
          {/* thumb + state halo (translate together) */}
          <span
            className={cn(
              'absolute left-1.5 top-1/2 -translate-y-1/2 transition-transform duration-200 motion-reduce:transition-none',
              checked && 'translate-x-6',
            )}
          >
            {!disabled && <span aria-hidden className={cn(SwitchHaloVariants({color}))} />}
            <span
              aria-hidden
              className={cn(
                'relative block size-5 rounded-full shadow-elevation-1',
                disabled ? 'bg-action-disabled' : 'bg-white',
              )}
            />
          </span>
        </button>
        {label &&
          (typeof label === 'string' ? (
            <span
              id={labelId}
              className={cn(
                'select-none text-base',
                checked ? 'font-bold' : 'font-normal',
                disabled ? 'text-text-disabled' : 'text-text-primary',
              )}
            >
              {label}
            </span>
          ) : (
            <span id={labelId}>{label}</span>
          ))}
      </div>
    )
  },
)

Switch.displayName = 'Switch'
